<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import {
  IconArchive,
  IconAttach,
  IconChatActive,
  IconEdit,
  IconUndo,
} from '@/components/design/icons';
import dayjs from 'dayjs';
import type { ChatThreadInDb } from '@/api';
import { formatModelLabel, formatModelProviderLabel } from '@/composables/useModelSelection';
import { parseReferencedItems } from '@/utils/chat/contextItems';
import GaiaConfirmCard from '../../composite/GaiaConfirmCard.vue';
import GaiaCreateButton from '../../base/GaiaCreateButton.vue';
import GaiaCountLabel from '../../base/GaiaCountLabel.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaListItem from '../../composite/GaiaListItem.vue';
import GaiaSearchInput from '../../base/inputs/GaiaSearchInput.vue';
import GaiaSectionHeader from '../../base/GaiaSectionHeader.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaTag from '../../base/GaiaTag.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaNeutral } from '../../themes/tokens';
import GaiaSwitch from '../../base/GaiaSwitch.vue';
import GaiaInlineEdit from '../../base/inputs/GaiaInlineEdit.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  threads: {
    type: Array as PropType<ChatThreadInDb[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Highlights the row for the thread currently open behind the modal. */
  activeUid: {
    type: String,
    default: '',
  },
  /** True when the chat behind the modal is already an untouched new thread. */
  newChatDisabled: {
    type: Boolean,
    default: false,
  },
  /** Archived chats are in `threads` — the host refetches when this flips. */
  archivedVisible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:show': [show: boolean];
  'update:archivedVisible': [visible: boolean];
  select: [threadUid: string];
  'new-chat': [];
  rename: [threadUid: string, title: string];
  archive: [threadUid: string, archived: boolean];
  delete: [threadUid: string];
}>();

const query = ref('');

/** Which row is mid-rename, and which is asking to be deleted. One at a time. */
const renamingUid = ref<string | null>(null);
const confirmingUid = ref<string | null>(null);

// A stale search term or a half-finished edit on reopen would hide threads the
// user just created, or resurrect a confirm they walked away from.
watch(
  () => props.show,
  isOpen => {
    if (!isOpen) return;
    query.value = '';
    renamingUid.value = null;
    confirmingUid.value = null;
  }
);

function threadTime(thread: ChatThreadInDb): dayjs.Dayjs {
  return dayjs(thread.updated_at ?? thread.created_at);
}

function threadTitle(thread: ChatThreadInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return thread.title?.trim() || 'Untitled chat';
}

const sortedThreads = computed(() =>
  [...props.threads].sort((a, b) => threadTime(b).valueOf() - threadTime(a).valueOf())
);

const hits = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!term) return sortedThreads.value;
  return sortedThreads.value.filter(thread => threadTitle(thread).toLowerCase().includes(term));
});

/** Today / Yesterday / Earlier, as in the design — each bucket keeps its order. */
const groups = computed(() => {
  const buckets: { label: string; threads: ChatThreadInDb[] }[] = [
    { label: 'Today', threads: [] },
    { label: 'Yesterday', threads: [] },
    { label: 'Earlier', threads: [] },
  ];
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  for (const thread of hits.value) {
    const day = threadTime(thread).startOf('day');
    const bucket = day.isSame(today) ? 0 : day.isSame(yesterday) ? 1 : 2;
    buckets[bucket].threads.push(thread);
  }

  return buckets.filter(bucket => bucket.threads.length > 0);
});

const countLabel = computed(() => `${hits.value.length} of ${props.threads.length}`);

/** "8 messages · 12:04" — today shows a clock, older rows show the date. */
function threadMeta(thread: ChatThreadInDb): string {
  const count = thread.human_message_count;
  const time = threadTime(thread);
  const when = time.isSame(dayjs(), 'day') ? time.format('HH:mm') : time.format('D MMM');
  return `${count} message${count === 1 ? '' : 's'} · ${when}`;
}

/** The model the thread is pinned to — set when it was created, never changed. */
function threadModel(thread: ChatThreadInDb): string {
  return formatModelLabel(thread.llm_model, thread.llm_provider);
}

/** "Google (EU) / Gemini 3.5 Flash" — the provider rides in the row's tooltip. */
function threadModelTitle(thread: ChatThreadInDb): string {
  const provider = formatModelProviderLabel(thread.llm_provider, thread.llm_model);
  return provider ? `${provider} / ${threadModel(thread)}` : threadModel(thread);
}

/** How many documents this conversation touched, for the row's paperclip. */
function referenceCount(thread: ChatThreadInDb): number {
  return parseReferencedItems(thread.thread_metadata).length;
}

function selectThread(thread: ChatThreadInDb): void {
  // A row being edited is not a row being picked.
  if (renamingUid.value === thread.uid || confirmingUid.value === thread.uid) return;
  emit('select', thread.uid);
  emit('update:show', false);
}

function startNewChat(): void {
  emit('new-chat');
  emit('update:show', false);
}

function startRename(thread: ChatThreadInDb): void {
  confirmingUid.value = null;
  renamingUid.value = thread.uid;
}

// The field focuses itself, keeps its own draft, and only commits a trimmed,
// changed, non-empty title — so all that is left is to send it.
function commitRename(title: string): void {
  const uid = renamingUid.value;
  renamingUid.value = null;
  if (uid) emit('rename', uid, title);
}

function cancelRename(): void {
  renamingUid.value = null;
}

function confirmDelete(uid: string): void {
  emit('delete', uid);
  confirmingUid.value = null;
}
/** Inline var — the modal root is a Teleport, so `v-bind()` never reaches it. */
const historyStyleVars = { '--chat-history-tint': gaiaNeutral.surfaceTint };
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="640px"
    :bordered="false"
    :style="historyStyleVars"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-header :level="2" size="page">Chats</gaia-header>
        <gaia-count-label>{{ countLabel }}</gaia-count-label>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-search-input v-model:value="query" variant="fill" placeholder="Search chats" />
        <!-- Slotted label: "New chat" is the idiom for a thread. -->
        <gaia-create-button
          :disabled="props.newChatDisabled"
          :title="props.newChatDisabled ? 'This is already a new chat' : 'Start a new chat'"
          :spam-proof="false"
          @click="startNewChat"
        >
          New chat
        </gaia-create-button>
      </n-flex>

      <n-flex align="center" :size="8" :wrap="false">
        <gaia-switch
          size="small"
          :value="props.archivedVisible"
          @update:value="(value: boolean) => emit('update:archivedVisible', value)"
        />
        <gaia-text size="dense" tone="tertiary">Show archived</gaia-text>
      </n-flex>

      <n-spin :show="props.loading">
        <n-scrollbar style="max-height: 46vh">
          <gaia-empty-state
            v-if="!hits.length"
            :title="query.trim() ? 'No chats match that search' : 'No chats yet'"
          >
            <template #icon>
              <n-icon :component="IconChatActive" :size="18" />
            </template>
            <template v-if="!query.trim()">Your questions will show up here.</template>
          </gaia-empty-state>

          <n-flex v-else vertical :size="2" data-tour="chat.threads">
            <template v-for="group in groups" :key="group.label">
              <gaia-section-header :style="{ padding: '14px 12px 6px' }">
                {{ group.label }}
              </gaia-section-header>

              <template v-for="thread in group.threads" :key="thread.uid">
                <!-- Deleting takes the row over rather than opening a dialog:
                     the thing being destroyed stays where the user pointed. -->
                <gaia-confirm-card
                  v-if="confirmingUid === thread.uid"
                  confirm-label="Delete"
                  @cancel="confirmingUid = null"
                  @confirm="confirmDelete(thread.uid)"
                >
                  Delete “{{ threadTitle(thread) }}”? The full conversation history and its
                  checkpoints go permanently.
                </gaia-confirm-card>

                <gaia-list-item
                  v-else
                  class="thread-list-item"
                  :class="{ active: thread.uid === props.activeUid, archived: thread.is_archived }"
                  @click="selectThread(thread)"
                >
                  <template #leading>
                    <n-icon :component="IconChatActive" :size="15" :color="gaiaNeutral.textMuted" />
                  </template>

                  <gaia-inline-edit
                    v-if="renamingUid === thread.uid"
                    :value="threadTitle(thread)"
                    size="small"
                    @commit="commitRename"
                    @cancel="cancelRename"
                  />
                  <n-flex v-else align="center" :size="8" :wrap="false">
                    <gaia-text size="body" weight="medium" tone="ink">
                      {{ threadTitle(thread) }}
                    </gaia-text>
                    <gaia-tag v-if="thread.is_archived" variant="muted" size="tiny">
                      Archived
                    </gaia-tag>
                  </n-flex>

                  <template #footer>
                    <n-flex align="center" :size="8" :wrap="false">
                      <gaia-text size="meta" tone="faint">{{ threadMeta(thread) }}</gaia-text>
                      <gaia-text size="meta" tone="faint">·</gaia-text>
                      <gaia-text
                        truncate
                        size="meta"
                        tone="faint"
                        :title="threadModelTitle(thread)"
                      >
                        {{ threadModel(thread) }}
                      </gaia-text>
                      <n-flex
                        v-if="referenceCount(thread)"
                        align="center"
                        :size="3"
                        :wrap="false"
                        :aria-label="`${referenceCount(thread)} documents used`"
                      >
                        <n-icon :component="IconAttach" :size="12" />
                        <gaia-text size="meta" tone="faint">{{ referenceCount(thread) }}</gaia-text>
                      </n-flex>
                    </n-flex>
                  </template>

                  <template #trailing>
                    <n-flex class="row-actions" align="center" :size="2" :wrap="false">
                      <gaia-icon-button
                        :dimension="28"
                        title="Rename"
                        aria-label="Rename this chat"
                        @click.stop="startRename(thread)"
                      >
                        <n-icon :component="IconEdit" :size="15" />
                      </gaia-icon-button>
                      <gaia-icon-button
                        :dimension="28"
                        :title="thread.is_archived ? 'Restore' : 'Archive'"
                        :aria-label="thread.is_archived ? 'Restore this chat' : 'Archive this chat'"
                        @click.stop="emit('archive', thread.uid, !thread.is_archived)"
                      >
                        <n-icon
                          :component="thread.is_archived ? IconUndo : IconArchive"
                          :size="15"
                        />
                      </gaia-icon-button>
                      <gaia-delete-button
                        size="md"
                        subject="chat"
                        @click.stop="confirmingUid = thread.uid"
                      />
                    </n-flex>
                  </template>
                </gaia-list-item>
              </template>
            </template>
          </n-flex>
        </n-scrollbar>
      </n-spin>
    </n-flex>
  </gaia-modal>
</template>

<style scoped>
/* The open thread's row. naive has no "selected list row" state and
   GaiaListItem only carries hover, so the one rule this file needs is the
   active tint. */
.active {
  background: var(--chat-history-tint);
  border-radius: 9px;
}

/* An archived chat is still readable, just demoted. */
.archived {
  opacity: 0.62;
}

/* Row actions stay out of the way until the row is pointed at — naive has no
   hover-revealed action affordance, so it is a rule of ours. `focus-within`
   keeps them reachable by keyboard, where there is no hover to trigger. */
.row-actions {
  opacity: 0;
  transition: opacity 120ms ease;
}

.thread-list-item:hover .row-actions,
.thread-list-item:focus-within .row-actions {
  opacity: 1;
}
</style>
